import React from 'react';
import {
    S17ToggleTabs,
    Tabs,
    MilanGradientColors,
    HelperTextProps,
    MilanHeadlineColors,
    MilanBackgroundColors,
    MilanIconColors,
    MilanTextColors,
    S17ToggleTabsProps,
} from '@milanlaser/mlcomponents';
import { processBackgroundAndGradientColorMap } from '../../utils/builder.utils';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { useFirebaseMetrics } from '../../utils/useFirebaseMetrics';

export interface S17Props {
    headlineText: string;
    headlineColor: MilanHeadlineColors;
    text1?: string;
    text2?: string;
    textColor: MilanTextColors;
    tabGroupProps: {
        icon: string; // URL of the icon image (SVG)
        label: string; // Label for the tab
        content: string; // Content for the tab
    }[];
    tabLabelColor?: MilanTextColors;
    tabParagraphColor?: MilanTextColors;
    iconColor?: MilanIconColors;
    backgroundColor?: MilanBackgroundColors;
    desktopPaddingTop: PaddingSize;
    desktopPaddingBottom: PaddingSize;
    mobilePaddingTop: PaddingSize;
    mobilePaddingBottom: PaddingSize;
    backgroundGradient: MilanGradientColors | 'None';
}

const helperTextDefaultProps: HelperTextProps = {
    headlineAlignment: 'center',
    headlineTag: 'h2',
    headlineVariant: 'h2-large',
    paragraphAlignment: 'left',
    paragraphColor: 'Navy',
    paragraphTag: 'p',
    paragraphVariant: 'p',
};

export function S17ToggleTabsSection(props: S17Props) {
    const { replaceVariables } = useFirebaseMetrics();
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
    } = props;

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const helperTextPropsFirst: HelperTextProps = {
        ...helperTextDefaultProps,
        headlineText: props.headlineText,
        paragraphText: replaceVariables(props.text1),
        headlineColor: props.headlineColor,
        paragraphColor: props.textColor,
    };

    const helperTextPropsSecond: HelperTextProps = {
        ...helperTextDefaultProps,
        paragraphText: replaceVariables(props.text2),
        paragraphColor: props.textColor,
    };

    const processedTabs = props.tabGroupProps.map(tab => ({
        ...tab,
        content: replaceVariables(tab.content)
    }));

    const tabGroupProps: Tabs = {
        tabs: processedTabs,
        tabLabelColor: props.tabLabelColor,
        textColor: props.tabParagraphColor,
    };

    const modifiedProps: S17ToggleTabsProps = {
        backgroundGradient:
            props.backgroundGradient === ('None' as MilanGradientColors | 'None')
                ? ('default' as MilanGradientColors)
                : (props.backgroundGradient as MilanGradientColors),
        backgroundColor: props.backgroundColor || 'default',

        helperTextPropsFirst,
        helperTextPropsSecond,
        tabGroupProps,
        iconColor: props.iconColor,
        className: `mcl-px-4 ${topPaddingClasses} ${bottomPaddingClasses}`,
    };

    const backgroundColor = processBackgroundAndGradientColorMap({
        backgroundColor: props.backgroundColor || 'default',
        gradientColor: props.backgroundGradient || 'default',
    });

    return (
        <div className={`${backgroundColor}`}>
            <S17ToggleTabs {...modifiedProps} />
        </div>
    );
}
